import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { useEffect, lazy, Suspense, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { motion, AnimatePresence } from 'framer-motion';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import AdminRoute from '@/components/AdminRoute';
// import ChatBot from './components/ChatBot';
import SalesAgentWidget from './components/SalesAgentWidget';
import CookieConsent from './components/CookieConsent';
import { trackPageView, trackPageExit } from '@/utils/analyticsTracking';
import { useScrollDepthTracking } from '@/hooks/useScrollDepthTracking';
import useStructuredData from '@/hooks/useStructuredData';
import { getSeoConfig, seoHeroImages, seoConfig } from '@/utils/seoConfig';
import { legacyRedirects } from '@/utils/legacyRedirects';
import ErrorBoundary from '@/components/ErrorBoundary';

const Home = lazy(() => import('./pages/Home'));
const OmOss = lazy(() => import('./pages/OmOss'));
const Process = lazy(() => import('./pages/Process'));
const SaunaCollectionLY = lazy(() => import('./pages/SaunaCollectionLY'));
const SaunaCollectionPanorama = lazy(() => import('./pages/SaunaCollectionPanorama'));
const SaunaCollectionFjel = lazy(() => import('./pages/SaunaCollectionFjel'));
const SaunaUmi = lazy(() => import('./pages/SaunaUmi'));
const SaunaMori = lazy(() => import('./pages/SaunaMori'));
const SaunaUmiKonfigurator = lazy(() => import('./pages/SaunaUmiKonfigurator'));
const SaunaHiroKonfigurator = lazy(() => import('./pages/SaunaHiroKonfigurator'));
const SaunaHiro = lazy(() => import('./pages/SaunaHiro'));
const SaunaYoriKonfigurator = lazy(() => import('./pages/SaunaYoriKonfigurator'));
const SaunaMoriKonfigurator = lazy(() => import('./pages/SaunaMoriKonfigurator'));
const SaunaToriKonfigurator = lazy(() => import('./pages/SaunaToriKonfigurator'));
const SaunaVerbier = lazy(() => import('./pages/SaunaVerbier'));
const SaunaTrysilKonfigurator = lazy(() => import('./pages/SaunaTrysilKonfigurator'));
const SaunaLivigno = lazy(() => import('./pages/SaunaLivigno'));
const SaunaAspen = lazy(() => import('./pages/SaunaAspen'));
const SaunaYori = lazy(() => import('./pages/SaunaYori'));
const SaunaTori = lazy(() => import('./pages/SaunaTori'));
const SaunaVerbierKonfigurator = lazy(() => import('./pages/SaunaVerbierKonfigurator'));
const SaunaCortina = lazy(() => import('./pages/SaunaCortina'));
const SaunaDolomiti = lazy(() => import('./pages/SaunaDolomiti'));
const SaunaVikka = lazy(() => import('./pages/SaunaVikka'));
const SaunaChamonix = lazy(() => import('./pages/SaunaChamonix'));
const SaunaTrysil = lazy(() => import('./pages/SaunaTrysil'));
const SaunaVikkaKonfigurator = lazy(() => import('./pages/SaunaVikkaKonfigurator'));
const SaunaGeilo = lazy(() => import('./pages/SaunaGeilo'));
const AdminCRM = lazy(() => import('./pages/AdminCRM.jsx'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminManagerDashboard = lazy(() => import('./pages/AdminManagerDashboard'));
const AdminConfigurator = lazy(() => import('./pages/AdminConfigurator'));
const AdminSaunaPricing = lazy(() => import('./pages/AdminSaunaPricing.jsx'));
const AdminMarginDashboard = lazy(() => import('./pages/AdminMarginDashboard'));
const AdminProfitDashboard = lazy(() => import('./pages/AdminProfitDashboard'));
const AdminScheduleCalendar = lazy(() => import('./pages/AdminScheduleCalendar'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminAnalyticsDashboard = lazy(() => import('./pages/AdminAnalyticsDashboard'));
const AdminAnalyticsSegmentation = lazy(() => import('./pages/AdminAnalyticsSegmentation'));
const AdminChat = lazy(() => import('./pages/AdminChat'));
const AdminQuoteCreator = lazy(() => import('./pages/AdminQuoteCreator'));
const AdminOrders = lazy(() => import('./pages/AdminOrders.jsx'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const AdminAccounting = lazy(() => import('./pages/AdminAccounting'));
const AdminFactoryInvoices = lazy(() => import('./pages/AdminFactoryInvoices'));
const AdminClientInvoices = lazy(() => import('./pages/AdminClientInvoices'));
const AdminProductionPlan = lazy(() => import('./pages/AdminProductionPlan'));
const AdminFactories = lazy(() => import('./pages/AdminFactories'));
const AdminAssemblyCalendar = lazy(() => import('./pages/AdminAssemblyCalendar.jsx'));
const AdminShipments = lazy(() => import('./pages/AdminShipments.jsx'));
const AdminQC = lazy(() => import('./pages/AdminQC.jsx'));
const AdminProductLifecycle = lazy(() => import('./pages/AdminPLM.jsx'));
const AdminDocsCatalog = lazy(() => import('./pages/AdminDocsCatalog.jsx'));
const AdminClaims = lazy(() => import('./pages/AdminClaims.jsx'));
const AdminCatalog = lazy(() => import('./pages/AdminCatalog'));
const AdminOfferCreator = lazy(() => import('./pages/AdminOfferCreator'));
const AdminContracts = lazy(() => import('./pages/AdminContracts'));
const AdminInvoices = lazy(() => import('./pages/AdminInvoices'));
const AdminSiteAudit = lazy(() => import('./pages/AdminSiteAudit'));
const AdminSalesAgent = lazy(() => import('./pages/AdminSalesAgent'));
const SaunaHytteKonfigurator = lazy(() => import('./pages/SaunaHytteKonfigurator'));
const SalesmanDashboard = lazy(() => import('./pages/SalesmanDashboard'));
const OfferView = lazy(() => import('./pages/OfferView'));
const ContractView = lazy(() => import('./pages/ContractView'));
const FactoryPhotoUpload = lazy(() => import('./pages/FactoryPhotoUpload.jsx'));
const ClientQCDocumentation = lazy(() => import('./pages/ClientQCDocumentation.jsx'));
const SaunaChamonixKonfigurator = lazy(() => import('./pages/SaunaChamonixKonfigurator'));
const SaunaCortinaKonfigurator = lazy(() => import('./pages/SaunaCortinaKonfigurator'));
const AdminManualSaunaConfigurator = lazy(() => import('./pages/AdminManualSaunaConfigurator'));
const SaunaNomad = lazy(() => import('./pages/SaunaNomad'));
const TinyBnb17 = lazy(() => import('./pages/TinyBnb17'));
const SaunaDolomitKonfigurator = lazy(() => import('./pages/SaunaDolomitKonfigurator'));
const SaunaGeiloKonfigurator = lazy(() => import('./pages/SaunaGeiloKonfigurator'));
const SaunaAspenKonfigurator = lazy(() => import('./pages/SaunaAspenKonfigurator'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const KebonyMaintenance = lazy(() => import('./pages/KebonyMaintenance'));
const Craftsmanship = lazy(() => import('./pages/Craftsmanship'));
const ProcessDetails = lazy(() => import('./pages/ProcessDetails'));
const ExteriorPainting = lazy(() => import('./pages/ExteriorPainting'));
const SaunaExterior = lazy(() => import('./pages/SaunaExterior'));
const Sertifikater = lazy(() => import('./pages/Sertifikater'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const RequestQuote = lazy(() => import('./pages/RequestQuote'));
const KebonyKled = lazy(() => import('./pages/KebonyKled'));
const LaerMerHandverk = lazy(() => import('./pages/LaerMerHandverk'));
const OljetTre = lazy(() => import('./pages/OljetTre'));
const PaintingMaintenance = lazy(() => import('./pages/PaintingMaintenance'));
const PaintProducts = lazy(() => import('./pages/PaintProducts'));
const Arkitektur = lazy(() => import('./pages/Arkitektur'));
const Fundament = lazy(() => import('./pages/Fundament'));
const RoofingMetal = lazy(() => import('./pages/RoofingMetal'));
const RoofingWood = lazy(() => import('./pages/RoofingWood'));
const RoofingGreen = lazy(() => import('./pages/RoofingGreen'));
const Transport = lazy(() => import('./pages/Transport'));
const InnvendigPanel = lazy(() => import('./pages/InnvendigPanel'));
const InnvendigGulv = lazy(() => import('./pages/InnvendigGulv'));
const InnvendigDorer = lazy(() => import('./pages/InnvendigDorer'));
const InnvendigVindu = lazy(() => import('./pages/InnvendigVindu'));
const InnvendigBelysning = lazy(() => import('./pages/InnvendigBelysning'));
const InnvendigEkstra = lazy(() => import('./pages/InnvendigEkstra'));

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();
  const location = useLocation();
  const pageStartTimeRef = useRef(Date.now());

  // Włączenie trackowania głębokości przewijania
  useScrollDepthTracking();

  // Wstrzykiwanie danych strukturalnych JSON-LD (schema.org)
  useStructuredData(location.pathname);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const pageStartTime = Date.now();
    pageStartTimeRef.current = pageStartTime;

    // Legacy PascalCase route (e.g. /Home, /SaunaHiro) → canonical lowercase URL.
    // window.location.replace leaves no history entry (acts like a 301).
    const legacyTarget = legacyRedirects[location.pathname];
    if (legacyTarget) {
      try { base44.analytics?.track?.({ eventName: 'legacy_redirect_hit', properties: { from: location.pathname, to: legacyTarget } }); } catch (e) { /* analytics optional */ }
      window.location.replace(legacyTarget);
      return;
    }

    // Determine if this is a genuine 404 (not a known content route and not a
    // protected/admin route which is handled separately by AdminRoute).
    const adminPrefixes = ['/admin', '/offer-view', '/contract-view', '/factory-photo-upload', '/client-qc-documentation', '/salesman-dashboard'];
    const isProtectedRoute = adminPrefixes.some(p => location.pathname.startsWith(p));
    const isKnownRoute = !!seoConfig[location.pathname];
    const isNotFound = !isKnownRoute && !isProtectedRoute;

    // Update SEO tags
    const seo = getSeoConfig(location.pathname);
    if (isNotFound) {
      document.title = 'Siden ble ikke funnet – LYKKE NOMAD';
    } else {
      document.title = seo.title;
    }

    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) descMeta.setAttribute('content', isNotFound ? '' : seo.description);

    // noindex for 404 pages so Google drops soft-404 (HTTP 200) entries like /Home
    let robotsMeta = document.querySelector('meta[name="robots"][data-seo-managed="404"]');
    if (isNotFound) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        robotsMeta.setAttribute('data-seo-managed', '404');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'noindex, nofollow');
    } else if (robotsMeta) {
      robotsMeta.remove();
    }
    
    if (seo.keywords) {
      let keywordsMeta = document.querySelector('meta[name="keywords"]');
      if (!keywordsMeta) {
        keywordsMeta = document.createElement('meta');
        keywordsMeta.setAttribute('name', 'keywords');
        document.head.appendChild(keywordsMeta);
      }
      keywordsMeta.setAttribute('content', seo.keywords);
    }
    
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', seo.title);
    
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', seo.description);
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `https://lykke.info${location.pathname}`);

    // Per-route hero image preload — keep <link rel="preload" as="image">
    // in sync with the actual LCP image of the current page.
    const heroHref = seoHeroImages[location.pathname];
    let preloadLink = document.querySelector('link[rel="preload"][as="image"]');
    if (heroHref) {
      if (!preloadLink) {
        preloadLink = document.createElement('link');
        preloadLink.setAttribute('rel', 'preload');
        preloadLink.setAttribute('as', 'image');
        document.head.appendChild(preloadLink);
      }
      if (preloadLink.getAttribute('href') !== heroHref) {
        preloadLink.setAttribute('href', heroHref);
      }
    } else if (preloadLink) {
      preloadLink.remove();
    }

    // Wysyłamy zdarzenie wejścia na stronę z pełnymi danymi UTM i sesji
    trackPageView();

    return () => {
      const timeSpent = Math.round((Date.now() - pageStartTime) / 1000);
      trackPageExit(timeSpent);
    };
  }, [location.pathname]);

  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to login automatically
      navigateToLogin();
      return null;
    }
  }

  // Render the main app
  return (
    <>
      {/* <ChatBot /> */}
      <SalesAgentWidget />
      <CookieConsent />
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          <ErrorBoundary>
          <Suspense fallback={<div className="fixed inset-0 flex items-center justify-center"><div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin" /></div>}>
          <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/om-oss" element={<OmOss />} />
      <Route path="/process" element={<Process />} />
      <Route path="/sauna-ly" element={<SaunaCollectionLY />} />
      <Route path="/sauna-panorama" element={<SaunaCollectionPanorama />} />
      <Route path="/sauna-fjel" element={<SaunaCollectionFjel />} />
      <Route path="/sauna-umi" element={<SaunaUmi />} />
      <Route path="/sauna-mori" element={<SaunaMori />} />
      <Route path="/sauna-umi-konfigurator" element={<SaunaUmiKonfigurator />} />
      <Route path="/sauna-hiro-konfigurator" element={<SaunaHiroKonfigurator />} />
      <Route path="/sauna-hiro" element={<SaunaHiro />} />
      <Route path="/sauna-yori-konfigurator" element={<SaunaYoriKonfigurator />} />
      <Route path="/sauna-mori-konfigurator" element={<SaunaMoriKonfigurator />} />
      <Route path="/sauna-tori-konfigurator" element={<SaunaToriKonfigurator />} />
      <Route path="/sauna-verbier" element={<SaunaVerbier />} />
      <Route path="/sauna-verbier-konfigurator" element={<SaunaVerbierKonfigurator />} />
      <Route path="/sauna-hytte" element={<SaunaLivigno />} />
      <Route path="/sauna-hytte-konfigurator" element={<SaunaHytteKonfigurator />} />
      <Route path="/sauna-aspen" element={<SaunaAspen />} />
      <Route path="/sauna-yori" element={<SaunaYori />} />
      <Route path="/sauna-tori" element={<SaunaTori />} />
      <Route path="/sauna-cortina" element={<SaunaCortina />} />
      <Route path="/sauna-dolomiti" element={<SaunaDolomiti />} />
      <Route path="/sauna-vikka" element={<SaunaVikka />} />
      <Route path="/sauna-chamonix" element={<SaunaChamonix />} />
      <Route path="/sauna-trysil" element={<SaunaTrysil />} />
      <Route path="/sauna-trysil-konfigurator" element={<SaunaTrysilKonfigurator />} />
      <Route path="/sauna-vikka-konfigurator" element={<SaunaVikkaKonfigurator />} />
      <Route path="/sauna-geilo" element={<SaunaGeilo />} />
      <Route path="/sauna-chamonix-konfigurator" element={<SaunaChamonixKonfigurator />} />
      <Route path="/sauna-cortina-konfigurator" element={<SaunaCortinaKonfigurator />} />
      <Route path="/sauna-dolomiti-konfigurator" element={<SaunaDolomitKonfigurator />} />
      <Route path="/sauna-geilo-konfigurator" element={<SaunaGeiloKonfigurator />} />
      <Route path="/sauna-aspen-konfigurator" element={<SaunaAspenKonfigurator />} />
      <Route path="/sauna-nomad" element={<SaunaNomad />} />
      <Route path="/tiny-bnb-17m" element={<TinyBnb17 />} />
      <Route path="/kebony-vedlikehold" element={<KebonyMaintenance />} />
      <Route path="/handverket" element={<Craftsmanship />} />
      <Route path="/process-details" element={<ProcessDetails />} />
      <Route path="/eksteriormaling" element={<ExteriorPainting />} />
      <Route path="/sauna-exterior" element={<SaunaExterior />} />
      <Route path="/sertifikater" element={<Sertifikater />} />
      <Route path="/portfolio" element={<Portfolio />} />
      <Route path="/be-om-tilbud" element={<RequestQuote />} />
      <Route path="/kebony-kled" element={<KebonyKled />} />
      <Route path="/laer-mer-handverk" element={<LaerMerHandverk />} />
      <Route path="/oljet-tre" element={<OljetTre />} />
      <Route path="/vedlikehold-maling" element={<PaintingMaintenance />} />
      <Route path="/produkter-maling" element={<PaintProducts />} />
      <Route path="/arkitektur" element={<Arkitektur />} />
      <Route path="/fundament" element={<Fundament />} />
      <Route path="/roofing-metal" element={<RoofingMetal />} />
      <Route path="/roofing-wood" element={<RoofingWood />} />
      <Route path="/roofing-green" element={<RoofingGreen />} />
      <Route path="/transport" element={<Transport />} />
      <Route path="/innvendig-panel" element={<InnvendigPanel />} />
      <Route path="/innvendig-gulv" element={<InnvendigGulv />} />
      <Route path="/innvendig-dorer" element={<InnvendigDorer />} />
      <Route path="/innvendig-vindu" element={<InnvendigVindu />} />
      <Route path="/innvendig-belysning" element={<InnvendigBelysning />} />
      <Route path="/innvendig-ekstra" element={<InnvendigEkstra />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route element={<AdminRoute />}>
      <Route path="/admin-dashboard" element={<AdminDashboard />} />
      <Route path="/admin-manager-dashboard" element={<AdminManagerDashboard />} />
      <Route path="/admin-crm" element={<AdminCRM />} />
      <Route path="/admin-configurator" element={<AdminConfigurator />} />
      <Route path="/admin-sauna-pricing" element={<AdminSaunaPricing />} />
      <Route path="/admin-margin-dashboard" element={<AdminMarginDashboard />} />
      <Route path="/admin-profit-dashboard" element={<AdminProfitDashboard />} />
      <Route path="/admin-schedule-calendar" element={<AdminScheduleCalendar />} />
      <Route path="/admin-analytics" element={<AdminAnalytics />} />
      <Route path="/admin-analytics-dashboard" element={<AdminAnalyticsDashboard />} />
      <Route path="/admin-analytics-segmentation" element={<AdminAnalyticsSegmentation />} />
      <Route path="/admin-chat" element={<AdminChat />} />
      <Route path="/admin-quote-creator" element={<AdminQuoteCreator />} />
      <Route path="/admin-orders" element={<AdminOrders />} />
      <Route path="/admin-orders/:orderId" element={<OrderDetail />} />
      <Route path="/admin-accounting" element={<AdminAccounting />} />
      <Route path="/admin-factory-invoices" element={<AdminFactoryInvoices />} />
      <Route path="/admin-client-invoices" element={<AdminClientInvoices />} />
      <Route path="/admin-production-plan" element={<AdminProductionPlan />} />
      <Route path="/admin-factories" element={<AdminFactories />} />
      <Route path="/admin-assembly-calendar" element={<AdminAssemblyCalendar />} />
      <Route path="/admin-shipments" element={<AdminShipments />} />
      <Route path="/admin-qc" element={<AdminQC />} />
      <Route path="/admin-plm" element={<AdminProductLifecycle />} />
      <Route path="/admin-docs-catalog" element={<AdminDocsCatalog />} />
      <Route path="/admin-claims" element={<AdminClaims />} />
      <Route path="/admin-catalog" element={<AdminCatalog />} />
      <Route path="/admin-offer-creator" element={<AdminOfferCreator />} />
      <Route path="/admin-contracts" element={<AdminContracts />} />
      <Route path="/admin-invoices" element={<AdminInvoices />} />
      <Route path="/admin-site-audit" element={<AdminSiteAudit />} />
      <Route path="/admin-sales-agent" element={<AdminSalesAgent />} />
      </Route>
      <Route path="/salesman-dashboard" element={<SalesmanDashboard />} />
      <Route path="/offer-view/:token" element={<OfferView />} />
      <Route path="/contract-view/:token" element={<ContractView />} />
      <Route element={<AdminRoute />}>
      <Route path="/factory-photo-upload" element={<FactoryPhotoUpload />} />
      <Route path="/client-qc-documentation" element={<ClientQCDocumentation />} />
      <Route path="/admin-manual-sauna-configurator" element={<AdminManualSaunaConfigurator />} />
      </Route>
            <Route path="*" element={<PageNotFound />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </motion.div>
      </AnimatePresence>
    </>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App